import React, { useState } from "react";
import {
  Button,
  message,
} from "antd";
import {
  ReloadOutlined,
  SettingOutlined,
  CloseOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { getAuthHeaders } from '../../services/clientDataService';
import ConfigureSemaforiModal from "./components/ConfigureSemaforiModal";
import ControlloOndaVerdeCard from "./components/ControlloOndaVerdeCard";
import SimulazioneModal from "./components/SimulazioneModal";
import "./tlgreenwave.css";

/**
 * TL Green Wave Component
 * Displays controls as an overlay on top of the existing map
 * This allows the street network data to remain visible
 */
function TlGreenWave({
  isDrawing,
  setIsDrawing,
  polygonPoints,
  trafficLights,
  isAddingManualPoint,
  setIsAddingManualPoint,
  onLoadTrafficLights,
  onClearAll,
  isLoadingCarica,
  isSelecting,
  setIsSelecting,
  selectedLights,
  onRemoveLight,
  onClearSelection,
  onStartDraw,
  onTrafficLightStateChange, // Callback to sync map markers with animation
  persistedSemaforiConfig,
  setPersistedSemaforiConfig,
  persistedLinksConfig,
  setPersistedLinksConfig,
  persistedVelocity,
  setPersistedVelocity,
  isEditing,
  onStartEdit,
}) {
  const [messageApi, contextHolder] = message.useMessage();

  // Modal and results state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showResultsCard, setShowResultsCard] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showSimulationModal, setShowSimulationModal] = useState(false);


  // Handle Termina button click - opens the config modal
  const handleTerminaClick = () => {
    if (!selectedLights || selectedLights.length < 2) {
      messageApi.warning("Seleziona almeno 2 semafori");
      return;
    }
    // Stop selection mode when opening config modal
    if (setIsSelecting) {
      setIsSelecting(false);
    }
    setShowConfigModal(true);
  };

  // Handle API call to calculate green wave offset
  const handleCalculateOffset = async (requestBody) => {
    setIsCalculating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/calcola_onda_verde`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to calculate offset');
      }

      const data = await response.json();
      // Enrich response with input parameters for simulation and display
      const enrichedData = {
        ...data,
        requestParams: requestBody
      };
      setResultsData(enrichedData);
      setShowConfigModal(false);
      setShowResultsCard(true);
      messageApi.success("Calcolo completato con successo");
    } catch (error) {
      console.error('Error calculating offset:', error);
      messageApi.error("Errore durante il calcolo dell'offset");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleCaricaClick = async () => {
    if (!polygonPoints || polygonPoints.length < 3) {
      messageApi.info("Per favore, disegna un poligono sulla mappa prima di caricare i semafori");
      return;
    }
    if (onLoadTrafficLights) {
      onLoadTrafficLights();
    }
  };

  const handleResetClick = () => {
    if (onClearAll) {
      onClearAll();
    }
    // Reset all results and config data
    setResultsData(null);
    setShowResultsCard(false);
    setShowConfigModal(false);
    setShowSimulationModal(false);
    if (setPersistedSemaforiConfig) setPersistedSemaforiConfig(null);
    if (setPersistedLinksConfig) setPersistedLinksConfig(null);
    if (setPersistedVelocity) setPersistedVelocity(50);
    messageApi.info("Semafori e poligono rimossi");
  };

  const handleDrawClick = () => {
    if (isDrawing) {
      // Stop drawing - this will be handled by Leaflet Draw
      if (setIsDrawing) {
        setIsDrawing(false);
      }
    } else {
      // Start drawing using Leaflet Draw
      if (onStartDraw) {
        onStartDraw();
      }
    }
  };



  // Selection is now auto-enabled when traffic lights are loaded (no button needed)

  // Get light details from trafficLights array
  const getSelectedLightDetails = () => {
    if (!selectedLights || !trafficLights) return [];
    return selectedLights.map(lightId => {
      const light = trafficLights.find(l => l.id === lightId);
      return light || { id: lightId };
    });
  };

  const selectedLightDetails = getSelectedLightDetails();

  return (
    <>
      {contextHolder}

      {/* Selection Card - Left side (hidden when config modal is open) */}
      {selectedLights && selectedLights.length > 0 && !showConfigModal && !showResultsCard && (
        <div className="tlgw-selection-card">
          <div className="tlgw-selection-header">
            <span className="tlgw-selection-title">
              Selezionati {selectedLights.length} semafori
            </span>
            <button
              className="tlgw-selection-close"
              onClick={onClearSelection}
              title="Chiudi"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="tlgw-selection-list">
            {selectedLightDetails.map((light, index) => (
              <div key={light.id} className="tlgw-selection-item">
                <span className="tlgw-selection-number">{index + 1}</span>
                <span className="tlgw-selection-id">{light.id}</span>
                <button
                  className="tlgw-selection-remove"
                  onClick={() => onRemoveLight && onRemoveLight(light.id)}
                  title="Rimuovi"
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
          </div>

          <div className="tlgw-selection-actions">
            <button
              className="tlgw-selection-btn tlgw-selection-btn-deselect"
              onClick={onClearSelection}
            >
              <CloseOutlined style={{ marginRight: 4 }} />
              Deseleziona
            </button>
            <button
              className="tlgw-selection-btn tlgw-selection-btn-confirm"
              onClick={handleTerminaClick}
            >
              Termina
            </button>
          </div>
        </div>
      )}

      {/* Polygon Controls - Leaflet Draw Style Toolbar */}
      <div className="tlgw-leaflet-toolbar">
        {/* Draw Polygon Button */}
        <button
          onClick={handleDrawClick}
          className={`tlgw-leaflet-btn ${isDrawing ? 'active' : ''}`}
          title={isDrawing ? "Annulla disegno" : "Disegna un poligono"}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1L1 6L3 15H13L15 6L8 1Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          </svg>
        </button>

        {/* Edit Polygon Button */}
        <button
          onClick={onStartEdit}
          className={`tlgw-leaflet-btn ${isEditing ? 'active' : ''} ${(!polygonPoints || polygonPoints.length === 0) ? 'disabled' : ''}`}
          title={isEditing ? "Salva modifiche" : "Modifica poligono"}
          disabled={!polygonPoints || polygonPoints.length === 0}
        >
          <EditOutlined />
        </button>

        {/* Delete Polygon Button */}
        <button
          onClick={() => {
            if (polygonPoints && polygonPoints.length > 0) {
              // Reset everything when area is deleted
              if (onClearAll) {
                onClearAll();
              }
              setResultsData(null);
              setShowResultsCard(false);
              setShowConfigModal(false);
              setShowSimulationModal(false);
              setPersistedSemaforiConfig(null);
              setPersistedLinksConfig(null);
              setPersistedVelocity(50);
            }
          }}
          className={`tlgw-leaflet-btn ${(!polygonPoints || polygonPoints.length === 0) ? 'disabled' : ''}`}
          title="Elimina livelli"
          disabled={!polygonPoints || polygonPoints.length === 0}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
          </svg>
        </button>
      </div>

      {/* TL Green Wave Control Bar - Right side */}
      <div className="tlgw-control-bar">
        <div className="tlgw-control-bar-inner">
          {/* Action Buttons */}
          <div className="tlgw-action-buttons">
            {/* Manual actions area - Separated with right border */}
            <div style={{ display: 'flex', gap: '8px', paddingRight: '12px', borderRight: '1px solid rgba(255,255,255,0.2)', marginRight: '4px' }}>
              <Button
                type="primary"
                size="large"
                style={{ background: '#353A0A', borderColor: '#353A0A' }}
                className="tlgw-action-button"
                onClick={() => setIsAddingManualPoint(true)}
                icon={<PlusOutlined />}
                disabled={trafficLights.length === 0}
                title="Aggiungi semaforo manuale"
              />

              <Button
                type="primary"
                size="large"
                style={{ background: '#353A0A', borderColor: '#353A0A' }}
                className="tlgw-action-button"
                onClick={() => setShowConfigModal(true)}
                icon={<SettingOutlined />}
                disabled={selectedLights.length === 0}
                title="Configura semafori"
              />
            </div>

            {/* Carica/Reset button */}
            <Button
              type="primary"
              size="large"
              style={{ background: '#353A0A', borderColor: '#353A0A' }}
              className="tlgw-action-button"
              onClick={trafficLights.length > 0 ? handleResetClick : handleCaricaClick}
              icon={trafficLights.length > 0 ? <ReloadOutlined /> : null}
              loading={isLoadingCarica}
              disabled={!polygonPoints || polygonPoints.length < 3}
            >
              {trafficLights.length > 0 ? 'RESET' : 'Carica'}
            </Button>

            {/* Simulazione button (after Carica) */}
            <Button
              type="primary"
              size="large"
              style={{ background: '#8C74FF', borderColor: '#8C74FF' }}
              className="tlgw-action-button"
              onClick={() => {
                setShowSimulationModal(true);
              }}
              disabled={!resultsData}
            >
              Simulazione
            </Button>
          </div>
        </div>
      </div>

      {/* Configure Semafori Modal */}
      <ConfigureSemaforiModal
        visible={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        selectedLights={selectedLights}
        trafficLights={trafficLights}
        onCalculate={handleCalculateOffset}
        isLoading={isCalculating}
        persistedSemaforiConfig={persistedSemaforiConfig}
        onSemaforiConfigChange={setPersistedSemaforiConfig}
        persistedLinksConfig={persistedLinksConfig}
        onLinksConfigChange={setPersistedLinksConfig}
        persistedVelocity={persistedVelocity}
        onVelocityChange={setPersistedVelocity}
      />

      {/* Results Card - Right side */}
      <ControlloOndaVerdeCard
        visible={showResultsCard}
        onClose={() => setShowResultsCard(false)}
        data={resultsData}
      />

      {/* Simulation Modal */}
      <SimulazioneModal
        visible={showSimulationModal}
        onClose={() => setShowSimulationModal(false)}
        simulationData={resultsData}
        velocity={resultsData?.requestParams?.vel_kmh || resultsData?.requestParams?.velocity || persistedVelocity || 50}
        onTrafficLightStateChange={onTrafficLightStateChange}
      />
    </>
  );
}

export default TlGreenWave;
